import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { fetchIngestStatus } from '@/api/ingest'
import { SortableTable } from '@/components/SortableTable'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'

const STATUS_RANK = { active: 0, stale: 1, never: 2 }

function formatWhen(iso) {
  if (!iso) return '—'
  return new Date(iso).toLocaleString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

function statusBadge(status) {
  if (status === 'active') {
    return { label: 'Receiving data', variant: 'success' }
  }
  if (status === 'stale') {
    return { label: 'Stale', variant: 'warning' }
  }
  return { label: 'No data yet', variant: 'secondary' }
}

function kindLabel(kind) {
  if (kind === 'snapshot') return 'Snapshot'
  if (kind === 'events') return 'Events'
  return kind
}

export function IngestStatus() {
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  function load() {
    setLoading(true)
    setError('')
    fetchIngestStatus()
      .then(setData)
      .catch(() => setError('Could not load RMM ingest status.'))
      .finally(() => setLoading(false))
  }

  useEffect(() => {
    load()
  }, [])

  const slugs = data?.slugs ?? []
  const activeCount = slugs.filter((s) => s.status === 'active').length
  const staleCount = slugs.filter((s) => s.status === 'stale').length
  const neverCount = slugs.filter((s) => s.status === 'never').length

  const tableRows = useMemo(
    () => slugs.map((row) => ({ ...row, id: row.slug })),
    [slugs],
  )

  const columns = useMemo(
    () => [
      {
        id: 'title',
        label: 'Script',
        sortValue: (row) => row.title,
        render: (row) => <span className="font-medium">{row.title}</span>,
      },
      {
        id: 'slug',
        label: 'Slug',
        sortValue: (row) => row.slug,
        render: (row) => (
          <code className="rounded bg-muted px-1 text-xs">{row.slug}</code>
        ),
      },
      {
        id: 'kind',
        label: 'Type',
        sortValue: (row) => kindLabel(row.kind),
        render: (row) => kindLabel(row.kind),
      },
      {
        id: 'notes',
        label: 'Notes',
        className: 'min-w-[14rem] max-w-[22rem]',
        sortValue: (row) => row.notes || '',
        render: (row) =>
          row.notes ? (
            <details className="text-xs text-muted-foreground">
              <summary className="cursor-pointer select-none text-foreground hover:underline">
                How collected
              </summary>
              <p className="mt-1.5 leading-snug">{row.notes}</p>
            </details>
          ) : (
            <span className="text-xs text-muted-foreground">—</span>
          ),
      },
      {
        id: 'status',
        label: 'Status',
        sortValue: (row) => STATUS_RANK[row.status] ?? 9,
        render: (row) => {
          const badge = statusBadge(row.status)
          return <Badge variant={badge.variant}>{badge.label}</Badge>
        },
      },
      {
        id: 'device_count',
        label: 'Devices',
        className: 'tabular-nums',
        sortValue: (row) => row.device_count,
        render: (row) => row.device_count.toLocaleString('en-GB'),
      },
      {
        id: 'linked_device_count',
        label: 'Linked',
        className: 'tabular-nums',
        sortValue: (row) => row.linked_device_count,
        render: (row) => row.linked_device_count.toLocaleString('en-GB'),
      },
      {
        id: 'coverage_pct',
        label: 'Coverage',
        className: 'tabular-nums',
        sortValue: (row) => row.coverage_pct,
        render: (row) => (row.coverage_pct != null ? `${row.coverage_pct}%` : '—'),
      },
      {
        id: 'last_reported_at',
        label: 'Last received',
        wrap: false,
        sortValue: (row) => row.last_reported_at,
        render: (row) => formatWhen(row.last_reported_at),
      },
      {
        id: 'last_data_at',
        label: 'Last data time',
        wrap: false,
        sortValue: (row) => row.last_data_at,
        render: (row) => formatWhen(row.last_data_at),
      },
      {
        id: 'event_count',
        label: 'Events',
        className: 'tabular-nums',
        sortValue: (row) => row.event_count,
        render: (row) =>
          row.event_count != null ? row.event_count.toLocaleString('en-GB') : '—',
      },
    ],
    [],
  )

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle>RMM ingest status</CardTitle>
          <CardDescription>
            Active means data or a script run heartbeat received within the last 2 days.
            Event scripts should POST every run, even when they find zero events today.
          </CardDescription>
        </CardHeader>
        <CardContent className="flex flex-wrap gap-4 text-sm">
          {data ? (
            <>
              <p>
                <span className="text-muted-foreground">Synced devices: </span>
                <span className="font-medium">{data.total_agents.toLocaleString('en-GB')}</span>
              </p>
              <p>
                <span className="text-muted-foreground">Active scripts: </span>
                <span className="font-medium">{activeCount}</span>
              </p>
              <p>
                <span className="text-muted-foreground">Stale: </span>
                <span className="font-medium">{staleCount}</span>
              </p>
              <p>
                <span className="text-muted-foreground">No data: </span>
                <span className="font-medium">{neverCount}</span>
              </p>
            </>
          ) : null}
        </CardContent>
      </Card>

      {error ? <p className="text-sm text-destructive">{error}</p> : null}

      <Card>
        <CardHeader className="flex flex-row items-center justify-between gap-4">
          <CardTitle className="text-base">Scripts and slugs</CardTitle>
          <button
            type="button"
            className="text-sm text-primary hover:underline"
            onClick={load}
          >
            Refresh
          </button>
        </CardHeader>
        <CardContent>
          {loading && !data ? (
            <Skeleton className="h-64 w-full" />
          ) : (
            <SortableTable
              columns={columns}
              data={tableRows}
              defaultSort={{ id: 'status', dir: 'asc' }}
              scrollable
              loading={loading}
              empty="No scripts registered."
            />
          )}
        </CardContent>
      </Card>

      <p className="text-sm text-muted-foreground">
        Scripts POST to{' '}
        <code className="rounded bg-muted px-1">/api/v1/ingest/snapshots</code> or{' '}
        <code className="rounded bg-muted px-1">/api/v1/ingest/events</code> with the slug in
        the JSON body. See also{' '}
        <Link to="/admin/settings" className="text-primary hover:underline">
          Settings
        </Link>{' '}
        for AI and server configuration.
      </p>
    </div>
  )
}
